import React, { useState, useEffect } from 'react'
import { Formik, Form, FormikHelpers as FormikActions, FormikErrors } from 'formik';
import { Link, useParams } from 'react-router-dom';

import {
  FormGroupBootstrap, ErrorBlockGeneric, Spoiler,
  LoadingCircle, ConfirmedButton, MdMath
} from '../components';
import {
  get_risposte_numeriche, get_mie_risposte, post_risposta, get_problema_thread,
} from '../api';
import { APIError, handle_drf_form_error } from 'django-rest-react';
import {
  nice_date,
  displayPartecipante, displayRisposta
} from '../utils';
import { Problema, Soluzione } from '../api/types';
import { canSeeRisposte, DECIMAL_PLACES, DECIMAL_DIGITS, login_url } from '../globals';
import { useLogin, usePartecipante } from '../reducers'



type RispostaForm = Parameters<typeof post_risposta>[0];
type FormValues = Omit<RispostaForm, "risposta"> & { risposta: number }
type Thread = Awaited<ReturnType<typeof get_problema_thread>>
type Risposta = Awaited<ReturnType<typeof get_risposte_numeriche>>[0]


const RispostaConsegnataFeed = (props: { risposta: RispostaNumerica, problema: Problema }) => {
  const login = useLogin();

  const { risposta, problema } = props;
  const pill = risposta.corretto ?
    <span className="badge badge-success">Corretto</span> :
    <span className="badge badge-danger">Sbagliato</span>;
  const label = problema?.soluzioni.find(i => (i.id == risposta.domanda))?.label || "";
  const link_partecipante = risposta.partecipante !== login.user.id ?
    <Link to={`/utente/${risposta.partecipante}/`}>
      {displayPartecipante(risposta.partecipante)}
    </Link> :
    "";

  const showRisposta = risposta.risposta !== undefined;

  return (
    <li>{nice_date(new Date(risposta.data))}:
      {link_partecipante} {pill} {label} {showRisposta && <Spoiler>{displayRisposta(risposta)}</Spoiler>}
    </li>
  );
}

type RispostaNumerica = Awaited<ReturnType<typeof get_risposte_numeriche>>[0]


const RispondiProblema = (props: { thread: Thread }) => {
  const { thread } = props;
  const partecipanti = usePartecipante(props);
  const [mie_risposte, setMieRisposte] = useState<Risposta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [risposte, setRisposte] = useState<Risposta[]>([]);

  const fetch_data = () => {
    const pk_domande = thread.problema.soluzioni.map(sol => sol.id);
    get_risposte_numeriche({ problema: thread.problema.id }).then(risposte => {
      setRisposte(risposte.filter(risp => (pk_domande.includes(risp.domanda))))
    });
    get_mie_risposte({ problema: thread.problema.id }).then(mie_risposte =>
      setMieRisposte(mie_risposte.filter(risp => pk_domande.includes(risp.domanda)))
    );
  }

  useEffect(() => {
    fetch_data()
  }, [thread.id])


  let risposte_vecchie;
  if (mie_risposte.length > 0) {
    risposte_vecchie = (
      <ul>
        {mie_risposte.map(risposta => <RispostaConsegnataFeed key={risposta.id} risposta={risposta} problema={thread.problema} />)}
      </ul>
    );
  } else {
    risposte_vecchie = (
      <p>Non hai ancora risposto a questo problema.</p>
    )
  }
  if (!thread) {
    return <LoadingCircle />
  }

  let may_i_hide_json = true;

  if (error) {
    if (Object.keys(error).filter(s => ["domanda", "risposta", "problema"].includes(s)).length > 0) {
      may_i_hide_json = false;
      // Se state.error contiene solo i campi "domanda", "risposta" e/o "problema" (la cui
      // visualizzazione è gestita dal form stesso), non mostrarlo nel blocco errore
    }
  }
  const { soluzioni } = thread.problema;
  const sol_iniziale = soluzioni.length == 1 ? soluzioni[0].id : null;

  return (
    <div className="container">
      <h1 className="page-header">Rispondi al problema</h1>
      <ErrorBlockGeneric error={error} hidejson={may_i_hide_json} />
      <h3>Le risposte che hai già dato</h3>
      {risposte_vecchie}
      <Formik
        initialValues={{
          risposta: 0, risposta_exp: 0,
          domanda: sol_iniziale as number,
        }}
        onSubmit={(values: FormValues, actions: FormikActions<FormValues>) => post_risposta({
          ...values,
          risposta: values.risposta.toFixed(DECIMAL_DIGITS),
        })
          .then(fetch_data)
          .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
          .finally(() => actions.setSubmitting(false))
        }
        enableReinitialize
      >
        {({ isSubmitting, handleReset, values }) => {
          const { soluzioni } = thread.problema;
          const domanda = soluzioni.find(item => item.id == values.domanda)
          return (
            <>
              <Form className="form needs-validation">
                <div className="row">
                  <div className="col">
                    <FormGroupBootstrap
                      name="risposta" type="number" step={DECIMAL_PLACES}
                      help_text={"Se una risposta è giusta o meno " +
                        "viene controllato vedendo se cade in una " +
                        "precisione moltiplicativa intorno alla risposta vera. " +
                        "La precisione è indicata qui sotto"
                      }
                    />
                  </div>
                  <div className="col">
                    <div className="row">
                      <p style={{ verticalAlign: 'bottom', marginTop: '15px', textAlign: 'right' }}>
                        ✕10
                      </p>
                      <div className="col-sm-3 ml-0">
                        <FormGroupBootstrap
                          name="risposta_exp" type="number" step={1}
                          displayName=" "
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <FormGroupBootstrap
                  name="domanda" type="select"
                  choices={soluzioni}
                  displayChoice={(item: Soluzione) => `${item.label} [${item.udm}]`}
                />
                <div className="text-center">
                  {domanda &&
                    <>
                      <MdMath source={domanda.udm || ""} />
                      <span>La precisione richiesta è {domanda.precisione}</span>
                    </>
                  }
                </div>
                <div className="d-flex justify-content-end">
                  <ConfirmedButton
                    type="warning"
                    onSubmit={handleReset}>
                    Reset
                  </ConfirmedButton>
                  <button type="submit" disabled={isSubmitting}
                    className="mx-1 btn btn-primary">Invia</button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
      <h3>Le risposte degli altri</h3>
      <ul>
        {risposte.map(risposta =>
          <RispostaConsegnataFeed key={risposta.id} risposta={risposta} problema={thread.problema} />)}
      </ul>
    </div>
  );
}


export default RispondiProblema;
